import { FC, Fragment } from 'react';
import type { GrupoSku, LinhaSku, SituacaoSku } from '@/utils/equiplexVisaoGeral';
import { fmtPct, fmtUn, fmtUnSinal } from '@/utils/painelFormat';
import { STATUS } from '../chartTheme';
import PainelCard from '../PainelCard';

interface Props {
  grupos: GrupoSku[];
  mesLabel: string;
}

const SITUACAO: Record<SituacaoSku, { rotulo: string; icone: string; cor: string } | null> = {
  no_plano: null,
  sem_plano: null,
  sem_producao: { rotulo: 'Sem produção', icone: 'block', cor: STATUS.serio },
  fora_do_plano: { rotulo: 'Fora do plano', icone: 'call_split', cor: STATUS.atencao },
};

const num = (v: number | null): string => (v === null ? '—' : fmtUn(v));

const Situacao: FC<{ s: SituacaoSku }> = ({ s }) => {
  const v = SITUACAO[s];
  if (!v) return null;
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-content-secondary">
      <span aria-hidden className="material-symbols-outlined text-[15px] leading-none" style={{ color: v.cor }}>
        {v.icone}
      </span>
      {v.rotulo}
    </span>
  );
};

const LinhaItem: FC<{ l: LinhaSku }> = ({ l }) => (
  <tr className="border-b border-theme-border">
    <td className="py-1.5 pl-4 pr-3 font-mono text-xs text-content-secondary">{l.codigoDatasul ?? l.codigo}</td>
    <td className="py-1.5 pr-3 text-content-primary">{l.descricao || '—'}</td>
    <td className="py-1.5 pr-3 text-right tabular-nums">{num(l.plano)}</td>
    <td className="py-1.5 pr-3 text-right tabular-nums">{fmtUn(l.realizado)}</td>
    <td className="py-1.5 pr-3 text-right tabular-nums">{fmtUnSinal(l.diferenca)}</td>
    <td className="py-1.5 pr-3 text-right tabular-nums">{fmtPct(l.pctDoPlano)}</td>
    <td className="py-1.5">
      <Situacao s={l.situacao} />
    </td>
  </tr>
);

/**
 * S&OP × realizado por SKU no mês, agrupado por formato. O subtotal do formato é a
 * comparação que vale quando plano e produção usam códigos diferentes para o mesmo
 * produto — as duas linhas aparecem lado a lado dentro do grupo.
 */
const SkuTable: FC<Props> = ({ grupos, mesLabel }) => (
  <PainelCard testId="vg-tabela-sku" titulo="S&OP × realizado por SKU" subtitulo={`${mesLabel}, agrupado por formato.`}>
    {grupos.length === 0 ? (
      <div className="py-10 text-center text-sm text-content-muted">Nem plano nem produção nos formatos escolhidos.</div>
    ) : (
      <div className="relative overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-theme-border text-left text-xs uppercase tracking-wide text-content-muted">
              <th className="py-2 pr-3 font-medium">Código</th>
              <th className="py-2 pr-3 font-medium">Descrição</th>
              <th className="py-2 pr-3 text-right font-medium">Plano S&OP</th>
              <th className="py-2 pr-3 text-right font-medium">Produzido</th>
              <th className="py-2 pr-3 text-right font-medium">Diferença</th>
              <th className="py-2 pr-3 text-right font-medium">% do plano</th>
              <th className="py-2 font-medium">
                <span className="sr-only">Situação</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {grupos.map((g) => (
              <Fragment key={g.categoria}>
                <tr className="border-b border-theme-border bg-surface-hover font-semibold text-content-primary" data-testid={`vg-grupo-${g.categoria}`}>
                  <td className="py-2 pr-3" colSpan={2}>
                    {g.categoria}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">{num(g.plano)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{fmtUn(g.realizado)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{fmtUnSinal(g.diferenca)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{fmtPct(g.pctDoPlano)}</td>
                  <td />
                </tr>
                {g.linhas.map((l) => (
                  <LinhaItem key={l.codigo} l={l} />
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </PainelCard>
);

export default SkuTable;
